import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import Drawer from "@/components/Drawer";
import { LoadingState } from "@/components/LoadingState";
import { orgApi } from "@/api/endpoints";
import type { Organisation, OrganisationSummary } from "@/types";

/**
 * Maps days-since-last-reading to a health indicator.
 * 🟢 green = active in last 7 days
 * 🟡 amber = 7-30 days
 * 🔴 red = >30 days
 * grey = never had a reading
 */
function healthIndicator(days: number | null | undefined): { color: string; label: string } {
  if (days == null) return { color: "bg-slate-300", label: "No readings yet" };
  if (days <= 7) return { color: "bg-green-500", label: `Active — ${days}d ago` };
  if (days <= 30) return { color: "bg-amber-500", label: `Stale — ${days}d ago` };
  return { color: "bg-red-500", label: `Dormant — ${days}d ago` };
}

export default function StaffOrganisationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OrganisationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const form = useForm<Partial<Organisation>>();

  const load = async () => {
    setLoading(true);
    setRows(await orgApi.list());
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = form.handleSubmit(async (values) => {
    try {
      await orgApi.create(values);
      toast.success("Organisation created");
      setOpen(false);
      form.reset();
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  });

  const columns: Column<OrganisationSummary>[] = [
    {
      key: "health",
      header: "",
      render: (o) => {
        const h = healthIndicator(o.days_since_last_reading);
        return <span className={`inline-block w-2.5 h-2.5 rounded-full ${h.color}`} title={h.label} />;
      },
      className: "w-8",
    },
    { key: "name", header: "Name", render: (o) => <span className="font-medium">{o.name}</span> },
    { key: "bldgs", header: "Buildings", render: (o) => o.building_count },
    { key: "users", header: "Users", render: (o) => o.user_count },
    {
      key: "last_reading",
      header: "Last reading",
      render: (o) => o.days_since_last_reading == null
        ? <span className="text-slate-400 text-xs">never</span>
        : <span className="text-xs text-slate-600">{o.days_since_last_reading}d ago</span>,
    },
    {
      key: "status", header: "Status",
      render: (o) => o.is_active ? <span className="badge bg-brand-50 text-brand-800">Active</span>
                                  : <span className="badge bg-slate-100 text-slate-600">Suspended</span>,
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Organisations"
        subtitle="All tenants on UmdoniWay"
        actions={
          <button className="btn-primary" type="button" onClick={() => setOpen(true)}>
            <Plus size={16} /> New organisation
          </button>
        }
      />
      {loading ? <LoadingState /> : (
        <DataTable
          columns={columns}
          rows={rows}
          onRowClick={(o) => navigate(`/staff/organisations/${o.id}`)}
        />
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Create organisation"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" type="button" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={submit}>Create</button>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="label">Name</label>
            <input className="input" {...form.register("name", { required: true })} />
          </div>
          <div>
            <label className="label">Industry</label>
            <input className="input" {...form.register("industry_type")} />
          </div>
          <div>
            <label className="label">Website</label>
            <input className="input" {...form.register("website")} />
          </div>
          <div>
            <label className="label">Billing email</label>
            <input type="email" className="input" {...form.register("billing_contact_email")} />
          </div>
        </form>
      </Drawer>
    </AppLayout>
  );
}
